import React, { useEffect, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import WhatsAppConnection from "components/whatsapp/WhatsAppConnection";
import { Link } from "react-router-dom";
import { orgApi } from "api/org";
import { playbooksApi } from "api/playbooks";
import { getCurrentUser } from "api/client";

function ProgressBar({ value, limit, label }) {
  const pct = limit > 0 ? Math.min(100, Math.round((value / limit) * 100)) : 0;
  const over = value > limit;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>{label}</span>
        <span
          className="font-caption text-xs font-medium"
          style={{ color: over ? "var(--color-error)" : "var(--color-foreground)" }}
        >
          {value.toLocaleString("en-IN")} / {limit.toLocaleString("en-IN")} tokens
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--color-muted)" }}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: over ? "var(--color-error)" : "var(--color-primary)" }}
        />
      </div>
    </div>
  );
}

export default function Settings() {
  const [org, setOrg] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ name: "", gstn: "", vertical: "", whatsappPhone: "" });

  const currentUser = getCurrentUser();
  const isOwner = currentUser?.role === "Owner";

  const load = async () => {
    setIsLoading(true);
    setError("");
    try {
      const [orgData, playbookItems] = await Promise.all([
        orgApi.getOrg(),
        playbooksApi.list(),
      ]);
      setOrg(orgData);
      setPlaybooks(playbookItems);
      setForm({
        name: orgData?.name || "",
        gstn: orgData?.gstn || "",
        vertical: orgData?.vertical || "",
        whatsappPhone: orgData?.whatsappPhone || "",
      });
    } catch (err) {
      setError(err?.message || "Unable to load settings.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError("");
    setNotice("");
    try {
      const patch = {};
      if (form.name.trim() && form.name.trim() !== org?.name) patch.name = form.name.trim();
      if (form.gstn.trim() !== (org?.gstn || "")) patch.gstn = form.gstn.trim();
      if (form.vertical !== org?.vertical) patch.vertical = form.vertical;
      if (form.whatsappPhone.trim() !== (org?.whatsappPhone || "")) patch.whatsappPhone = form.whatsappPhone.trim();
      if (Object.keys(patch).length > 0) {
        await orgApi.updateOrg(patch);
        setNotice("Workspace details saved.");
      }
      await load();
    } catch (err) {
      setError(err?.message || "Unable to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const ai = org?.aiUsage;
  const verticalLabel = playbooks.find((p) => p.id === org?.vertical)?.label || org?.vertical || "Not set";

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
            Workspace settings
          </h1>
          <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            Your org profile, WhatsApp channel and AI cost guardrails
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{error}</p>
          </div>
        )}
        {notice && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "#BBF7D0", background: "#F0FDF4" }}>
            <p className="font-caption text-sm font-medium" style={{ color: "#15803D" }}>{notice}</p>
          </div>
        )}

        {isLoading ? (
          <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading…</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Workspace details */}
            <div className="rounded-xl border p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Building2" size={16} color="var(--color-primary)" />
                <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>Workspace details</h2>
              </div>
              <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
                {isOwner ? "Only the Owner can change these." : "Managed by the Owner."}
              </p>
              <form onSubmit={handleSave} className="space-y-3">
                <div>
                  <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>Company name</label>
                  <input
                    className="w-full text-sm px-3 py-2 rounded-lg border font-caption"
                    style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
                    value={form.name}
                    disabled={!isOwner}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>GSTIN</label>
                  <input
                    className="w-full text-sm px-3 py-2 rounded-lg border font-caption"
                    style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
                    value={form.gstn}
                    disabled={!isOwner}
                    placeholder="Optional"
                    onChange={(e) => setForm((f) => ({ ...f, gstn: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>Vertical / playbook</label>
                  <select
                    className="w-full text-sm px-3 py-2 rounded-lg border font-caption"
                    style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
                    value={form.vertical}
                    disabled={!isOwner}
                    onChange={(e) => setForm((f) => ({ ...f, vertical: e.target.value }))}
                  >
                    {playbooks.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>WhatsApp number</label>
                  <input
                    className="w-full text-sm px-3 py-2 rounded-lg border font-caption"
                    style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
                    value={form.whatsappPhone}
                    disabled={!isOwner}
                    placeholder="e.g. +919876543210"
                    onChange={(e) => setForm((f) => ({ ...f, whatsappPhone: e.target.value }))}
                  />
                </div>
                {isOwner && (
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? "Saving…" : "Save changes"}
                  </Button>
                )}
              </form>
            </div>

            {/* AI budget */}
            <div className="rounded-xl border p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Zap" size={16} color="var(--color-primary)" />
                <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>AI cost guardrails</h2>
              </div>
              <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
                Live token spend for this workspace — draft generation, follow-ups and playbooks.
              </p>
              {ai ? (
                <>
                  <ProgressBar label="Today" value={ai.todayTokens} limit={ai.dailyLimit} />
                  <ProgressBar label="This month" value={ai.monthTokens} limit={ai.monthlyLimit} />
                  <p className="font-caption text-xs mt-3" style={{ color: "var(--color-text-secondary)" }}>
                    When a limit is reached, AI drafting pauses until the window resets — messaging never breaks.
                  </p>
                </>
              ) : (
                <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>No spend recorded yet.</p>
              )}
            </div>

            {/* WhatsApp channel + plan */}
            <div className="rounded-xl border p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="MessageCircle" size={16} color="var(--color-primary)" />
                <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>WhatsApp channel</h2>
              </div>
              <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
                The phone number customers message, your WhatsApp Business API credentials and a live connection test —
                all in one place.
              </p>
              <WhatsAppConnection />
              <div className="flex items-center gap-2 mt-4">
                <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>Plan:</span>
                <span className="inline-block px-2 py-0.5 rounded-full font-caption text-[11px] font-medium" style={{ background: "var(--color-muted)", color: "var(--color-foreground)" }}>
                  {org?.plan || "—"}
                </span>
              </div>
              <p className="font-caption text-xs mt-3" style={{ color: "var(--color-text-secondary)" }}>
                Vertical: {verticalLabel}
              </p>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}